import {
  LOGIN_JWT_SUCCESS,
  TOGGLE_JWT_MODAL,
  LOGIN_JWT_FAIL,
  START_JWT_LOADING,
  GET_JWT_PROFILE,
  GET_JWT_PROFILE_FAIL,
  TOGGLE_USER_MODAL,
  LOGOUT_JWT,
  SUCCESS_SIGNUP_JWT,
  REGISTER_JWT_FAIL,
  END_JWT_LOADING,
  UPDATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_FAIL,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAIL,
  DELETE_ACCOUNT_SUCCESS,
  REQUEST_PASSWORD_RESET_SUCCESS,
  REQUEST_PASSWORD_RESET_FAIL,
  SET_LOGIN_MODAL_VIEW
} from "../type";
import {
  loginApi,
  signupApi,
  logoutApi,
  requestPasswordResetApi,
  updateProfileApi,
  setPasswordApi,
  deleteAccountApi,
  getStoredTokens,
  storeTokens,
  clearTokens,
} from "../../services/api.services";
import * as legacyAuth from "../../services/authJWT.services";
import { serverResponse } from "../../util/fakeServer";
import { messageError, hasNewCheckout } from "../../helper/utility";

// ===== AUTH ACTIONS =====

// Login legacy (tenant senza x_client_token): vecchio backend /viewmanager/api,
// auth a cookie. Nessun JWT: il reducer salva solo user = payload.
const loginLegacy = (data, history) => async (dispatch) => {
  try {
    const res = await legacyAuth.login({
      ...data,
      signboard_id: serverResponse.config.signboard_id,
    });
    const { status, data: payload } = res?.data || {};

    if (status === 200) {
      dispatch({ type: LOGIN_JWT_SUCCESS, payload });
      if (history) history("/leaflet");
    } else {
      dispatch({ type: LOGIN_JWT_FAIL, payload: messageError(status) });
    }
  } catch (err) {
    dispatch({ type: LOGIN_JWT_FAIL, payload: messageError(err && err.status) });
  }
};

export const loginJWT = (data, history) => async (dispatch) => {
  dispatch({ type: START_JWT_LOADING });

  // Retrocompatibilita': token assente -> vecchio flusso inshop.
  if (!hasNewCheckout()) {
    return dispatch(loginLegacy(data, history));
  }

  try {
    const res = await loginApi(data.email, data.password);

    if (res.status === 200 && res.data) {
      const { jwt_token, refresh_token, expires_in, user } = res.data;
      const info = user?.info || {};

      const tokens = {
        jwtToken: jwt_token,
        refreshToken: refresh_token,
        expiresAt: Date.now() + expires_in * 1000,
      };
      storeTokens(tokens);

      dispatch({
        type: LOGIN_JWT_SUCCESS,
        payload: {
          id: user?.id_user,
          apiUserId: user?.id_user,
          idCliente: user?.id_cliente,
          name: info.nome,
          surname: info.cognome,
          email: info.email,
          phone: info.telefono,
          address: info.indirizzo,
          jwtToken: jwt_token,
          refreshToken: refresh_token,
          expiresAt: tokens.expiresAt,
        },
      });

      if (history) history("/leaflet");
    } else {
      dispatch({
        type: LOGIN_JWT_FAIL,
        payload: res.message || messageError(res.status),
      });
    }
  } catch (err) {
    dispatch({
      type: LOGIN_JWT_FAIL,
      payload: messageError(),
    });
  }
};

// Mappa chiave-form -> chiave attesa dal v2 /platform-user/signup (doc OpenAPI).
// Whitelist: SOLO queste chiavi vengono inviate; confirm_password, privacy, ecc.
// restano client-side. Vale per tutti i form dei manager (standard, Troilo, ...).
const SIGNUP_FIELD_MAP = {
  name: "nome",
  surname: "cognome",
  email: "email",
  password: "password",
  phone: "telefono",
  address: "indirizzo",
  comune: "comune",
  provincia: "provincia",
  cap: "cap",
  ragione_sociale: "ragionesociale",
  partita_iva: "iva",
  sdi: "sdi",
  pec: "pec",
  codice: "codice",
  extra1: "extra1",
};

// Costruisce il payload v2 dai campi PRESENTI nel form (qualunque manager),
// includendo solo i valori valorizzati + landing. Ripristina il comportamento
// "dinamico" di produzione sul nuovo endpoint /platform-user/signup.
const buildSignupPayload = (data = {}) => {
  const payload = {};
  Object.keys(SIGNUP_FIELD_MAP).forEach((formKey) => {
    const v = data[formKey];
    if (v !== undefined && v !== null && v !== "") {
      payload[SIGNUP_FIELD_MAP[formKey]] = v;
    }
  });
  payload.landing = window.location.href;
  return payload;
};

// Signup legacy (tenant senza x_client_token): vecchio backend, payload
// "grezzo" con signboard_id/id_client come da produzione pre-v2.
const signupLegacy = (data) => async (dispatch) => {
  try {
    const res = await legacyAuth.signup({
      ...data,
      signboard_id: serverResponse.config.signboard_id,
      id_client: serverResponse.config.client_id,
      landing: window.location.href,
    });
    const { status } = res?.data || {};

    if (status === 200) {
      dispatch({ type: SUCCESS_SIGNUP_JWT });
    } else {
      dispatch({ type: REGISTER_JWT_FAIL, payload: messageError(status) });
    }
  } catch (err) {
    dispatch({ type: REGISTER_JWT_FAIL, payload: messageError(err && err.status) });
  }
};

export const signupJWT = (data) => async (dispatch) => {
  dispatch({ type: START_JWT_LOADING });

  // Retrocompatibilita': token assente -> vecchio flusso inshop.
  if (!hasNewCheckout()) {
    return dispatch(signupLegacy(data));
  }

  try {
    const res = await signupApi(buildSignupPayload(data));

    if (res.status === 200) {
      dispatch({ type: SUCCESS_SIGNUP_JWT });
    } else {
      dispatch({
        type: REGISTER_JWT_FAIL,
        payload: res.message || messageError(res.status),
      });
    }
  } catch (err) {
    dispatch({
      type: REGISTER_JWT_FAIL,
      payload: messageError(),
    });
  }
};

// Logout legacy (tenant senza x_client_token): invalida il cookie sul vecchio
// backend, poi pulisce lo stato locale.
const logoutLegacy = (history) => async (dispatch) => {
  try {
    await legacyAuth.logout();
  } catch { /* ignore */ }
  clearTokens();
  dispatch({ type: LOGOUT_JWT });
  if (history) {
    history(serverResponse.config.only_logged ? "/login" : "/leaflet");
  }
};

export const logoutJWT = (history) => (dispatch) => {
  // Retrocompatibilita': token assente -> vecchio flusso inshop (cookie).
  if (!hasNewCheckout()) {
    return dispatch(logoutLegacy(history));
  }

  clearTokens();
  dispatch({ type: LOGOUT_JWT });

  if (history) {
    history(serverResponse.config.only_logged ? "/login" : "/leaflet");
  }

  // Logout API in background (non-blocking)
  try {
    const tokens = getStoredTokens();
    if (tokens?.jwtToken) {
      logoutApi(tokens.jwtToken, tokens.refreshToken).catch(() => {});
    }
  } catch { /* ignore */ }
};

// Ripristino sessione legacy (tenant senza x_client_token): auth a cookie, il
// backend risolve l'utente dalla sessione. Nessun JWT in localStorage.
const getProfileLegacy = () => async (dispatch) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const splitParam = code ? code.split("?")[0] : "";

    const res = await legacyAuth.getProfile(splitParam);
    const { status, data: payload } = res?.data || {};

    if (status === 200) {
      dispatch({ type: GET_JWT_PROFILE, payload });
    } else {
      dispatch({ type: GET_JWT_PROFILE_FAIL });
    }
  } catch (err) {
    dispatch({ type: GET_JWT_PROFILE_FAIL });
  }
};

export const getProfileJWT = () => (dispatch) => {
  // Retrocompatibilita': token assente -> ripristino via cookie legacy.
  if (!hasNewCheckout()) {
    return dispatch(getProfileLegacy());
  }

  const tokens = getStoredTokens();

  // If valid tokens + saved user exist, restore session from localStorage
  if (tokens?.jwtToken && tokens?.expiresAt > Date.now()) {
    try {
      const savedUser = localStorage.getItem("inpublish_auth_user");
      if (savedUser) {
        dispatch({
          type: GET_JWT_PROFILE,
          payload: {
            ...JSON.parse(savedUser),
            jwtToken: tokens.jwtToken,
            refreshToken: tokens.refreshToken,
            expiresAt: tokens.expiresAt,
          },
        });
        return;
      }
    } catch { /* ignore */ }
  }

  // No valid session
  clearTokens();
  dispatch({ type: GET_JWT_PROFILE_FAIL });
};

// ===== UI ACTIONS =====

export const startLoadingJWT = () => (dispatch) => {
  dispatch({ type: START_JWT_LOADING });
};

export const endLoadingJWT = () => (dispatch) => {
  dispatch({ type: END_JWT_LOADING });
};

export const toggleJWTModal = () => (dispatch) => {
  dispatch({ type: TOGGLE_JWT_MODAL });
};

export const toggleUserModal = () => (dispatch) => {
  dispatch({ type: TOGGLE_USER_MODAL });
};

export const setLoginModalView = (view) => (dispatch) => {
  dispatch({ type: SET_LOGIN_MODAL_VIEW, payload: view });
};

// ===== PROFILE ACTIONS =====

export const updateProfile = (data) => async (dispatch) => {
  dispatch({ type: START_JWT_LOADING });

  try {
    const res = await updateProfileApi({
      nome: data.name,
      cognome: data.surname,
      email: data.email,
    });

    if (res.status === 200) {
      dispatch({
        type: UPDATE_PROFILE_SUCCESS,
        payload: data,
      });
    } else {
      dispatch({
        type: UPDATE_PROFILE_FAIL,
        payload: res.message || "Errore aggiornamento profilo",
      });
    }
  } catch (err) {
    dispatch({
      type: UPDATE_PROFILE_FAIL,
      payload: "Errore aggiornamento profilo",
    });
  }
};

export const changePassword = (data) => async (dispatch) => {
  dispatch({ type: START_JWT_LOADING });

  try {
    const res = await setPasswordApi(data.password);

    if (res.status === 200) {
      dispatch({ type: CHANGE_PASSWORD_SUCCESS });
    } else {
      dispatch({
        type: CHANGE_PASSWORD_FAIL,
        payload: res.message || "Errore cambio password",
      });
    }
  } catch (err) {
    dispatch({
      type: CHANGE_PASSWORD_FAIL,
      payload: "Errore cambio password",
    });
  }
};

export const deleteAccount = (history) => async (dispatch) => {
  dispatch({ type: START_JWT_LOADING });

  try {
    const res = await deleteAccountApi();

    if (res.status === 200) {
      clearTokens();
      dispatch({ type: DELETE_ACCOUNT_SUCCESS });
      if (history) history("/login");
    } else {
      dispatch({ type: END_JWT_LOADING });
    }
  } catch (err) {
    dispatch({ type: END_JWT_LOADING });
  }
};

export const requestPasswordReset = (email) => async (dispatch) => {
  dispatch({ type: START_JWT_LOADING });

  try {
    const res = await requestPasswordResetApi(
      email,
      `${window.location.origin}${window.location.pathname.replace(/\/[^/]*$/, "")}/set-psw`,
      window.location.origin
    );

    if (res.status === 200) {
      dispatch({ type: REQUEST_PASSWORD_RESET_SUCCESS });
    } else {
      dispatch({
        type: REQUEST_PASSWORD_RESET_FAIL,
        payload: res.message || "Errore invio email",
      });
    }
  } catch (err) {
    dispatch({
      type: REQUEST_PASSWORD_RESET_FAIL,
      payload: "Errore invio email",
    });
  }
};
